import { Routes } from '@angular/router';
import { AuthGuard } from './guards/auth.guard';
import { GuestGuard } from './guards/guest.guard';
import { unsavedChangesGuard } from './guards/unsaved-changes.guard';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./welcome/welcome.component').then(m => m.WelcomeComponent),
    pathMatch: 'full'
  },
  {
    path: 'login',
    loadComponent: () => import('./auth/login.component').then(m => m.LoginComponent),
    canActivate: [GuestGuard]
  },
  {
    path: 'signup',
    loadComponent: () => import('./auth/signup.component').then(m => m.SignupComponent),
    canActivate: [GuestGuard]
  },
  {
    path: 'verify-email',
    loadComponent: () => import('./auth/verify-email.component').then(m => m.VerifyEmailComponent),
    canActivate: [GuestGuard]
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./auth/forgot-password.component').then(m => m.ForgotPasswordComponent),
    canActivate: [GuestGuard]
  },
  {
    path: 'dashboard',
    loadComponent: () => import('./dashboard/dashboard.component').then(m => m.DashboardComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'my-profile',
    loadComponent: () => import('./my-profile/my-profile.component').then(m => m.MyProfileComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'faq',
    loadComponent: () => import('./faq/faq.component').then(m => m.FaqComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'auction-current',
    loadComponent: () => import('./auction-current/auction-current.component').then(m => m.AuctionCurrentComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'costume-ideas',
    loadComponent: () => import('./costume-ideas/costume-ideas.component').then(m => m.CostumeIdeasComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'event-ideas',
    loadComponent: () => import('./event-ideas/event-ideas.component').then(m => m.EventIdeasComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'tickets',
    loadComponent: () => import('./tickets/tickets.component').then(m => m.TicketsComponent),
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard]
  },
  {
    path: 'my-tickets',
    loadComponent: () => import('./my-tickets/my-tickets.component').then(m => m.MyTicketsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'my-donations',
    loadComponent: () => import('./my-donations/my-donations.component').then(m => m.MyDonationsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'donation',
    loadComponent: () => import('./donation/donation.component').then(m => m.DonationComponent),
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard]
  },
  {
    path: 'donation/review/:itemId',
    loadComponent: () => import('./donation/donation.component').then(m => m.DonationComponent),
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    data: { role: 'admin' }
  },
  {
    path: 'donation/edit/:itemId',
    loadComponent: () => import('./donation/donation.component').then(m => m.DonationComponent),
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    data: { role: 'admin' }
  },
  {
    path: 'donation/view/:itemId',
    loadComponent: () => import('./donation/donation.component').then(m => m.DonationComponent),
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    data: { role: 'admin' }
  },
  {
    path: 'review-donations',
    loadComponent: () => import('./review-donations/review-donations.component').then(m => m.ReviewDonationsComponent),
    canActivate: [AuthGuard],
    data: { role: 'admin' }
  },
  {
    path: 'preview-items',
    loadComponent: () => import('./preview-items/preview-items.component').then(m => m.PreviewItemsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'reports',
    loadComponent: () => import('./reports/reports.component').then(m => m.ReportsComponent),
    canActivate: [AuthGuard],
    data: { role: 'analyst' }
  },
  {
    path: 'reports/ticket-sales',
    loadComponent: () => import('./reports/ticket-sales/ticket-sales-report.component').then(m => m.TicketSalesReportComponent),
    canActivate: [AuthGuard],
    data: { role: 'analyst' }
  },
  {
    path: 'reports/donation-analytics',
    loadComponent: () => import('./reports/donation-analytics/donation-analytics-report.component').then(m => m.DonationAnalyticsReportComponent),
    canActivate: [AuthGuard],
    data: { role: 'analyst' }
  },
  {
    path: 'reports/financial',
    loadComponent: () => import('./reports/financial-report/financial-report.component').then(m => m.FinancialReportComponent),
    canActivate: [AuthGuard],
    data: { role: 'analyst' }
  },
  {
    path: 'reports/invoices',
    loadComponent: () => import('./reports/invoices-report/invoices-report.component').then(m => m.InvoicesReportComponent),
    canActivate: [AuthGuard],
    data: { role: 'analyst' }
  },
  {
    path: 'reports/bids',
    loadComponent: () => import('./reports/bids-report/bids-report.component').then(m => m.BidsReportComponent),
    canActivate: [AuthGuard],
    data: { role: 'analyst' }
  },
  {
    path: 'reports/bar-donations',
    loadComponent: () => import('./reports/bar-donations-report/bar-donations-report.component').then(m => m.BarDonationsReportComponent),
    canActivate: [AuthGuard],
    data: { role: 'analyst' }
  },
  {
    path: 'bid-entry',
    loadComponent: () => import('./bid-entry/bid-entry.component').then(m => m.BidEntryComponent),
    canActivate: [AuthGuard],
    data: { role: 'admin' }
  },
  {
    path: 'my-invoices',
    loadComponent: () => import('./invoices/invoices.component').then(m => m.InvoicesComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'admin-invoices',
    loadComponent: () => import('./admin-invoices/admin-invoices.component').then(m => m.AdminInvoicesComponent),
    canActivate: [AuthGuard],
    data: { role: 'superuser' }
  },
  {
    path: 'bidder-contacts',
    loadComponent: () => import('./bidder-contacts/bidder-contacts.component').then(m => m.BidderContactsComponent),
    canActivate: [AuthGuard],
    data: { role: 'superuser' }
  },
  {
    path: 'admin/sell-tickets',
    loadComponent: () => import('./admin/sell-tickets.component').then(m => m.SellTicketsComponent),
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    data: { role: 'admin' }
  },
  {
    path: 'admin/cash-bidder',
    loadComponent: () => import('./admin/add-cash-bidder.component').then(m => m.AddCashBidderComponent),
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    data: { role: 'admin' }
  },
  {
    path: 'admin/separate-accounts',
    loadComponent: () => import('./admin/separate-accounts.component').then(m => m.SeparateAccountsComponent),
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    data: { role: 'admin' }
  },
  {
    path: 'print-pdfs',
    loadComponent: () => import('./print-pdfs/print-pdfs.component').then(m => m.PrintPdfsComponent),
    canActivate: [AuthGuard],
    data: { role: 'admin' }
  },
  {
    path: 'live-catalog-preview',
    loadComponent: () => import('./live-catalog-preview/live-catalog-preview.component').then(m => m.LiveCatalogPreviewComponent),
    canActivate: [AuthGuard],
    data: { role: 'admin' }
  },
  {
    path: 'unauthorized',
    loadComponent: () => import('./unauthorized/unauthorized.component').then(m => m.UnauthorizedComponent)
  },
  {
    path: '**',
    redirectTo: '/'
  }
];
